import React, { useEffect, useState } from "react";
import API from "../../api";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Search } from "lucide-react";

const ProjectShareOnly = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [technology, setTechnology] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const allCategories = [
    "Agency", "Banking", "Billing System", "Blog", "Construction",
    "Consulting", "Corporate", "CRM", "Digital Marketing", "Domain Management",
    "Educational", "Entertainment", "E-Commerce", "ERP", "Event Management",
    "Finance", "Fitness", "Food Delivery", "Government", "Healthcare",
    "Hosting", "Hospital", "Inventory Management", "Landing Page", "Logistics",
    "Manufacturing", "MLM", "Mobile App", "News Portal", "NGO", "Other",
    "Portfolio", "POS System", "Real Estate", "Retail", "SEO", "Server Maintenance",
    "Software", "Startup", "Travel", "Web App", "Website", "Wholesale"
  ].sort();

  const allTechnologies = [
    "React", "Node.js", "Angular", "Vue", "Laravel", "PHP", "Python", "Django",
    "Flask", "Java", "Spring", "C#", ".NET", "WordPress", "Shopify", "Other"
  ].sort();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/admin/projects", {
        params: { search, category, technology, statusFilter },
      });
      setProjects(data.projects || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      alert("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [search, category, technology, statusFilter]);

  const handleShareWhatsApp = (domain) => {
    const message = `🌐 Project Domain: ${domain}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const badgeClass = (status) => {
    switch (status) {
      case "expired":
        return "bg-red-100 text-red-600";
      case "expire soon":
        return "bg-yellow-100 text-yellow-600";
      default:
        return "bg-green-100 text-green-600";
    }
  };

  const formatIndiaDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">Shareable Projects</h1>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-4 gap-2">
        <div className="relative w-full sm:w-1/3">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by project, customer, domain..."
            className="pl-9 pr-3 py-2 border rounded-lg w-full focus:ring focus:ring-blue-200"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="border rounded-lg px-3 py-2 w-full sm:w-1/5"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Select Category</option>
          {allCategories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <select
          className="border rounded-lg px-3 py-2 w-full sm:w-1/5"
          value={technology}
          onChange={(e) => setTechnology(e.target.value)}
        >
          <option value="">Select Technology</option>
          {allTechnologies.map((tech) => (
            <option key={tech} value={tech}>{tech}</option>
          ))}
        </select>

        <select
          className="border rounded-lg px-3 py-2 w-full sm:w-1/5"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="expire soon">Expire Soon</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm text-left border-collapse">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 border">S.No.</th>
              <th className="px-4 py-2 border">Customer</th>
              <th className="px-4 py-2 border">Project</th>
              <th className="px-4 py-2 border">Category</th>
              <th className="px-4 py-2 border">Technology</th>
              <th className="px-4 py-2 border">Domain</th>
              <th className="px-4 py-2 border">Purchase Date</th>
              <th className="px-4 py-2 border">Status</th>
              <th className="px-4 py-2 border text-center">Share</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-gray-500">Loading...</td>
              </tr>
            ) : projects.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-gray-500">No projects found.</td>
              </tr>
            ) : (
              projects.map((p, index) => (
                <tr key={p.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-2 border font-medium">{index + 1}</td>
                  <td className="px-4 py-2 border">{p.customer_name}</td>
                  <td className="px-4 py-2 border">{p.project_name}</td>
                  <td className="px-4 py-2 border">{p.project_category}</td>
                  <td className="px-4 py-2 border">{p.project_technology}</td>
                  <td className="px-4 py-2 border">{p.domain_name}</td>
                  <td className="px-4 py-2 border">{formatIndiaDate(p.domain_purchase_date)}</td>
                  <td className="px-4 py-2 border">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${badgeClass(p.domain_status)}`}>
                      {p.domain_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 border text-center">
                    <button
                      onClick={() => handleShareWhatsApp(p.domain_name)}
                      className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200"
                      title="Share via WhatsApp"
                    >
                      <FontAwesomeIcon icon={faWhatsapp} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectShareOnly;
